/**
 * Capital gains rate brackets — mirrors TaxReturnUtils.calculateCapGainsRate()
 * Source: TaxReturnUtils.java (dated 16 May 2025)
 */

import { TaxBracket } from './federal-tax-brackets';

// ─── 2025 Capital Gains Brackets ────────────────────────────────────

export const CAP_GAINS_BRACKETS_2025: { [key: string]: TaxBracket[] } = {
  'Single': [
    { upTo: 49450, rate: 0.00 },
    { upTo: 545500, rate: 0.15 },
    { upTo: Infinity, rate: 0.20 },
  ],
  'Married Filing Separately (MFS)': [
    { upTo: 49450, rate: 0.00 },
    { upTo: 306850, rate: 0.15 },
    { upTo: Infinity, rate: 0.20 },
  ],
  'Married Filing Jointly (MFJ)': [
    { upTo: 98900, rate: 0.00 },
    { upTo: 613700, rate: 0.15 },
    { upTo: Infinity, rate: 0.20 },
  ],
  'Head of Household': [
    { upTo: 66200, rate: 0.00 },
    { upTo: 579600, rate: 0.15 },
    { upTo: Infinity, rate: 0.20 },
  ],
  'Estate': [
    { upTo: 3300, rate: 0.00 },
    { upTo: 16250, rate: 0.15 },
    { upTo: Infinity, rate: 0.20 },
  ],
  'Trust': [
    { upTo: 3300, rate: 0.00 },
    { upTo: 16250, rate: 0.15 },
    { upTo: Infinity, rate: 0.20 },
  ],
};

/**
 * Get capital gains brackets for a given year.
 */
export function getCapGainsBrackets(year: number): { [key: string]: TaxBracket[] } {
  return CAP_GAINS_BRACKETS_2025;
}

/**
 * Get the capital gains rate for a given taxable income (marginal rate).
 */
export function getCapGainsRate(taxableIncome: number, filingStatus: string): number {
  const brackets = getCapGainsBrackets(new Date().getFullYear())[filingStatus];
  if (!brackets || taxableIncome <= 0) return 0;

  let rate = 0;
  for (const bracket of brackets) {
    rate = bracket.rate;
    if (taxableIncome <= bracket.upTo) break;
  }
  return rate * 100; // return as percentage
}

/**
 * Compute capital gains tax dollars using progressive brackets.
 */
export function computeCapGainsTax(longTermGains: number, taxableIncome: number, filingStatus: string): number {
  const brackets = getCapGainsBrackets(new Date().getFullYear())[filingStatus];
  if (!brackets || longTermGains <= 0 || taxableIncome <= 0) return 0;

  // Capital gains are stacked on top of ordinary income.
  // The ordinary income "fills up" the lower brackets first.
  const ordinaryIncome = taxableIncome - longTermGains;
  let tax = 0;
  let previousUpTo = 0;

  for (const bracket of brackets) {
    // How much of this bracket is available after ordinary income fills it
    const bracketStart = Math.max(previousUpTo, ordinaryIncome);
    const bracketEnd = Math.min(taxableIncome, bracket.upTo);
    const taxableInBracket = bracketEnd - bracketStart;
    if (taxableInBracket > 0) {
      tax += taxableInBracket * bracket.rate;
    }
    previousUpTo = bracket.upTo;
    if (taxableIncome <= bracket.upTo) break;
  }

  return Math.round(tax * 100) / 100;
}

/**
 * NIIT (Net Investment Income Tax): 3.8% on investment income above threshold.
 * Not in the existing backend but is standard IRS computation.
 */
export const NIIT_RATE = 0.038;

export const NIIT_THRESHOLDS: { [key: string]: number } = {
  'Single': 200000,
  'Married Filing Jointly (MFJ)': 250000,
  'Married Filing Separately (MFS)': 125000,
  'Head of Household': 200000,
  'Estate': 13450,  // 2025 threshold for estates/trusts
  'Trust': 13450,
};

/**
 * Compute NIIT tax.
 * NIIT = 3.8% * min(net investment income, AGI - threshold)
 */
export function computeNIIT(netInvestmentIncome: number, agi: number, filingStatus: string): number {
  const threshold = NIIT_THRESHOLDS[filingStatus] || 200000;
  const excess = agi - threshold;
  if (excess <= 0 || netInvestmentIncome <= 0) return 0;
  const taxableAmount = Math.min(netInvestmentIncome, excess);
  return Math.round(taxableAmount * NIIT_RATE * 100) / 100;
}
